// @ts-nocheck
import AddToCartButtonWithIcon, {
    AddToCartButtonWithIconStyles,
} from "@store/Library/Components/Common/AddToCartButtonWithIcon";
import PriceMatrix, { PriceMatrixStyles } from "@store/Library/Components/Common/PriceMatrix";
import useProductContext from "@store/Framework/Store/Data/Products/ProductsHooks/useProductContext";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import Clickable from "@insite/mobius/Clickable/Clickable";
import Icon, { IconMemo, IconProps } from "@insite/mobius/Icon";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Link, { LinkPresentationProps, LinkProps } from "@store/MobiusOverrides/Link/Link";
import { ProductAvailabilityStyles } from "@store/Overrides/Components/ProductAvailability";
import ProductQuantityOrdered from "@store/Overrides/Components/ProductQuantityOrdered";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { productLineProps } from "@store/Themes/common/props/productLineProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import CheckmarkCircle2Fill from "@toolkit/Library/Components/Icons/CheckmarkCircle2/CheckmarkCircle2Fill";
import Refresh from "@toolkit/Library/Components/Icons/Refresh";
import React from "react";
import { FC } from "react";
import { css } from "styled-components";

interface MyListDetailsProductControlsMobileProps {
    canEditWishList: boolean;
    onUpdateQuantityClick: () => void;
    isQuantityUpdated: boolean;
    extendedPrice: number;
    wishListLine: WishListLineModel;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
}
interface MyListDetailsProductControlsMobileStyles {
    mobileProductControls?: InjectableCss;
    productControlsItem?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    mobileQtyAndExtendedPrice?: InjectableCss;
    productQuantityOrderedStyles?: TextFieldProps;
    updateQuantityWrapper?: InjectableCss;
    extendedMobilePrice?: TypographyProps;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: IconProps;
    quantityUpdatedText?: TypographyProps;
    addToCartButton?: AddToCartButtonWithIconStyles;
    priceMatrixMobile?: PriceMatrixStyles;
    deleteButtonWrapper?: InjectableCss;
    removeProductButton?: LinkProps;
    removeListLineIcon?: IconProps;
    // bunzl custom Code
    addToCartAndAddToListRow?: InjectableCss;
}

const myListDetailsProductControlsMobileStyles: MyListDetailsProductControlsMobileStyles = {
    updateQuantityLink: {
        typographyProps: {
            css: css`
                word-break: keep-all;
                font-size: ${x => x.theme.design.typography.size.smallest};
            `,
        },
        icon: {
            iconProps: {
                src: Refresh,
                size: 16,
                color: "secondary",
            },
        },
        css: css`
            ${linkStyles()}
        `,
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    quantityUpdatedIcon: {
        src: CheckmarkCircle2Fill,
        size: 16,
        color: "success.main",
        css: css`
            margin-right: ${pxToRem(5)};
        `,
    },
    productAvailabilityStyles: productLineProps.productAvailability,
    productQuantityOrderedStyles: productLineProps.quantityOrdered,
    extendedMobilePrice: {
        css: css`
            color: ${getColor("text.milanoRed")};
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};
        `,
    },
    productControlsItem: productLineProps.mobileProductControlsItem,
    mobileProductControls: productLineProps.mobileProductControls,
    priceMatrixMobile: {
        wrapper: {
            css: css`
                margin-top: 0.5rem;
            `,
        },
    },
    removeProductButton: {
        css: css`
            ${productLineProps.removeProductButton?.css}
        `,
    },
    removeListLineIcon: productLineProps.removeProductLineIcon,
    addToCartAndAddToListRow: productLineProps.addToCartAndAddToListRow,
    deleteButtonWrapper: {
        css: css`
            ${breakpoint("sm", "down")} {
                position: absolute;
                right: 0;
                top: 0.5rem;
            }
            min-width: ${pxToRem(36)};
            margin-block: auto;
            margin-left: ${pxToRem(16)};
        `,
    },
};
const styles = myListDetailsProductControlsMobileStyles;
const MyListDetailsProductControlsMobile: FC<MyListDetailsProductControlsMobileProps> = ({
    canEditWishList,
    onUpdateQuantityClick,
    extendedPrice,
    isQuantityUpdated,
    wishListLine,
    onDeleteClick,
}) => {
    const { product, productInfo } = useProductContext();
    const productSettings = useSettingsCollection().productSettings;
    return (
        <StyledWrapper {...styles.mobileProductControls}>
            {productSettings.showInventoryAvailability && product.trackInventory ? (
                <StyledWrapper {...styles.productControlsItem}>
                    <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.productControlsItem}>
                    <Typography>{translate("Inventory Unavailable")}</Typography>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.productControlsItem}>
                <ProductQuantityOrdered
                    extendedStyles={styles.productQuantityOrderedStyles}
                    labelOverride={false}
                    canEdit={true}
                />

                <StyledWrapper {...styles.productControlsItem}>
                    <Typography {...styles.extendedMobilePrice}>
                        <LocalizedCurrency amount={extendedPrice} />
                    </Typography>
                </StyledWrapper>
                {canEditWishList && productInfo.qtyOrdered > 0 && wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                    <Link {...styles.updateQuantityLink} onClick={() => onUpdateQuantityClick()}>
                        {translate("Update QTY")}
                    </Link>
                )}
            </StyledWrapper>

            {isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                <>
                    <Icon {...styles.quantityUpdatedIcon} />
                    <Typography {...styles.quantityUpdatedText}>{translate("QTY updated")}</Typography>
                </>
            )}
            <StyledWrapper {...styles.addToCartAndAddToListRow}>
                <AddToCartButtonWithIcon
                    notes={wishListLine.notes}
                    extendedStyles={styles.addToCartButton}
                    data-test-selector="addToCart"
                />
                <>
                    {canEditWishList && (
                        <Clickable
                            {...styles.removeProductButton}
                            onClick={() => onDeleteClick(wishListLine)}
                            data-test-selector="removeRestricted"
                        >
                            <IconMemo {...styles.removeListLineIcon} />
                            <VisuallyHidden>{translate("Removed this product")}</VisuallyHidden>
                        </Clickable>
                    )}
                </>
            </StyledWrapper>
            <PriceMatrix extendedStyles={styles.priceMatrixMobile} />
        </StyledWrapper>
    );
};

export default MyListDetailsProductControlsMobile;
